import React, { useState } from 'react';
import { Typography, Box, Link as MuiLink, Stack, Divider } from '@mui/material';
import GoogleLoginButton from '../../components/button/GoogleLoginButton';
import NaverLoginButton from '../../components/button/NaverLoginButton';
import KakaoLoginButton from '../../components/button/KakaoLoginButton';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi } from '../../api/auth';
import '../../styles/Login.css';
import { useUserStore } from '../../stores/userStore';
import queryClient from '../../queryClient';
import PageLayout from '../../components/layout/PageLayout';
import ActionDialog from '../../components/dialog/ActionDialog';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import BasicButton from '../../components/button/BasicButton';
import theme from '../../theme';
import CustomTextField from '../../components/common/CustomTextField';
import CustomCheckbox from '../../components/common/CustomCheckbox';
import { safeRedirectPath } from '../../utils/safeRedirectPath';

const schema = yup.object().shape({
  email: yup.string().required('이메일을 입력해 주세요.'),
  password: yup.string().required('비밀번호를 입력해 주세요.'),
});

const Login = () => {
  const [searchParams] = useSearchParams();
  const redirectUrl = safeRedirectPath(searchParams.get('redirectUrl'));

  const socialLoginButtons = [
    { name: 'Google', component: <GoogleLoginButton redirectUrl={redirectUrl} /> },
    { name: 'Naver', component: <NaverLoginButton redirectUrl={redirectUrl} /> },
    { name: 'Kakao', component: <KakaoLoginButton redirectUrl={redirectUrl} /> },
  ];

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const navigate = useNavigate();
  const fetchUser = useUserStore((state) => state.fetchUser);

  const onSubmit = async (formData) => {
    try {
      const { data } = await authApi.login(formData);
      if (data && data.isActive === false) {
        setInfoDialogOpen(true);
      } else if (data && data.token) {
        localStorage.setItem('POSTPOP_AUTH_TOKEN', data.token);
        queryClient.clear();
        await fetchUser();

        navigate(redirectUrl, { replace: true });
        reset(); // 로그인 성공 시 폼 초기화
      }
    } catch (error) {
      console.error(error);
      // 로그인 실패 시 비밀번호 필드만 지움
      reset({ email: formData.email, password: '', rememberMe: formData.rememberMe });
    }
  };

  return (
    <PageLayout centerContent={true}>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            border: { xs: 0, md: 1, lg: 1 },
            borderColor: { md: theme.palette.grey[300], lg: theme.palette.grey[300] },
            borderRadius: 1,
            width: { lg: '42.5%', md: '66%', xs: '100%' },
            minWidth: { xs: '320px' },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              marginTop: { lg: '50px', md: '40px', xs: '0px' },
              marginBottom: { lg: '40px', md: '30px', xs: '0px' },
              marginLeft: { lg: '50px', md: '30px', xs: '0px' },
              marginRight: { lg: '50px', md: '30px', xs: '0px' },
            }}
          >
            <Stack spacing={{ lg: '50px', md: '50px', xs: '40px' }}>
              <Typography
                fontWeight="bold"
                textAlign="center"
                sx={{
                  fontSize: { xs: 20, md: 22, lg: 28 },
                }}
              >
                Welcome to postpop
              </Typography>

              {/* 이메일, 비밀번호 입력 */}
              <Stack spacing={{ lg: '30px', md: '20px', xs: '20px' }} alignItems="center">
                <Stack spacing={2.5} sx={{ width: '100%' }}>
                  <Stack spacing={1}>
                    <Typography variant="body3">이메일</Typography>
                    <CustomTextField
                      {...register('email')}
                      placeholder="이메일을 입력해 주세요."
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  </Stack>
                  <Stack spacing={1}>
                    <Typography variant="body3">비밀번호</Typography>
                    <CustomTextField
                      {...register('password')}
                      type="password"
                      placeholder="비밀번호를 입력해 주세요."
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                  </Stack>
                </Stack>

                <Box sx={{ width: '100%' }}>
                  <CustomCheckbox {...register('rememberMe')} label="로그인 상태 유지" />
                </Box>

                <BasicButton
                  type="submit"
                  fullWidth
                  disabled={isSubmitting}
                  onClick={handleSubmit(onSubmit)}
                  sx={{ height: '48px' }}
                >
                  로그인
                </BasicButton>
              </Stack>
            </Stack>

            {/* 간편 로그인 */}
            <Box
              mt={{ lg: '30px', md: '20px', xs: '20px' }}
              paddingTop={'20px'}
              paddingBottom={'20px'}
              paddingLeft={{ lg: '20px', md: '20px', xs: '0px' }}
              paddingRight={{ lg: '20px', md: '20px', xs: '0px' }}
            >
              <Stack spacing={2.5} alignItems="center">
                <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                  <Divider sx={{ flex: 1 }} />
                  <Typography variant="body3">간편 로그인</Typography>
                  <Divider sx={{ flex: 1 }} />
                </Stack>
                <Stack direction="row" spacing={3.75}>
                  {socialLoginButtons.map((button) => (
                    <React.Fragment key={button.name}>{button.component}</React.Fragment>
                  ))}
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 회원가입, 비밀번호 찾기 */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        spacing={0.25}
        sx={{ mt: { lg: '30px', md: '20px', xs: '40px' } }}
        minWidth={'320px'}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body3">아직 회원이 아니신가요?</Typography>
          <MuiLink
            underline="always"
            onClick={() =>
              navigate(
                redirectUrl ? `/signup?redirectUrl=${encodeURIComponent(redirectUrl)}` : '/signup',
              )
            }
            sx={{ cursor: 'pointer', fontSize: 14 }}
          >
            회원가입
          </MuiLink>
        </Stack>
        <Box sx={{ pr: 0.5, px: 0.5 }}>
          <Divider orientation="vertical" sx={{ height: 8 }} />
        </Box>
        <MuiLink
          underline="none"
          onClick={() => navigate('/password-forgot')}
          sx={{ cursor: 'pointer', fontSize: 14 }}
        >
          비밀번호 찾기
        </MuiLink>
      </Stack>

      <ActionDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        onConfirm={() => setInfoDialogOpen(false)}
        showCancel={false}
      >
        <Typography sx={{ textAlign: 'center', lineHeight: 1.5 }}>
          관리자에 의해 활동이 정지되었습니다
          <br />더 자세한 내용은 이메일을 확인해주세요.
        </Typography>
      </ActionDialog>
    </PageLayout>
  );
};

export default Login;
